/**
 * CrewBulkRatesModal
 *
 * Sets default pay rates on every selected crew member at once. Each rate is
 * opt-in: an unticked rate is not sent and stays whatever it was per person,
 * so an hourly change never wipes someone's daily rate. A ticked rate left
 * blank clears it back to "not set" (which reporting flags), and the modal
 * says so before the admin commits. Dollars in the form, cents on the wire,
 * like the single-crew form.
 */

import { FormEvent, useEffect, useState } from 'react';
import { FormModal } from '../ui';
import { parseDollars } from '../../utils/rateFormatting';
import type { BulkRatesInput } from '../../api/users';

interface CrewBulkRatesModalProps {
  isOpen: boolean;
  selectedCount: number;
  isSubmitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (rates: BulkRatesInput) => void;
}

export default function CrewBulkRatesModal({
  isOpen,
  selectedCount,
  isSubmitting = false,
  error = null,
  onClose,
  onSubmit,
}: CrewBulkRatesModalProps) {
  const [changeHourly, setChangeHourly] = useState(false);
  const [changeDaily, setChangeDaily] = useState(false);
  const [hourly, setHourly] = useState('');
  const [daily, setDaily] = useState('');

  // Fresh form each time it opens; a stale "clear hourly" from last time
  // would be an unpleasant surprise.
  useEffect(() => {
    if (isOpen) {
      setChangeHourly(false);
      setChangeDaily(false);
      setHourly('');
      setDaily('');
    }
  }, [isOpen]);

  const hourlyBlank = hourly.trim() === '';
  const dailyBlank = daily.trim() === '';
  const hourlyCents = parseDollars(hourly);
  const dailyCents = parseDollars(daily);
  const hourlyInvalid = changeHourly && !hourlyBlank && (hourlyCents === null || hourlyCents < 0);
  const dailyInvalid = changeDaily && !dailyBlank && (dailyCents === null || dailyCents < 0);
  const nothingChosen = !changeHourly && !changeDaily;

  const clearing = [changeHourly && hourlyBlank && 'hourly', changeDaily && dailyBlank && 'daily'].filter(
    (x): x is string => typeof x === 'string'
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (nothingChosen || hourlyInvalid || dailyInvalid) return;
    const rates: BulkRatesInput = {};
    if (changeHourly) rates.defaultHourlyRate = hourlyBlank ? null : hourlyCents;
    if (changeDaily) rates.defaultDailyRate = dailyBlank ? null : dailyCents;
    onSubmit(rates);
  };

  const noun = selectedCount === 1 ? 'crew member' : 'crew members';

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Update pay rates"
      submitLabel={`Update ${selectedCount} ${noun}`}
      isSubmitting={isSubmitting}
      isSubmitDisabled={nothingChosen || hourlyInvalid || dailyInvalid}
      error={error}
    >
      <p className="text-sm text-gray-500">
        Tick a rate to set it on all {selectedCount} selected {noun}. An unticked rate
        is left as it is for each person. Event role overrides are not affected.
      </p>

      <RateRow
        id="bulkHourlyRate"
        label="Hourly rate"
        suffix="/hr"
        enabled={changeHourly}
        onToggle={setChangeHourly}
        value={hourly}
        onChange={setHourly}
        invalid={hourlyInvalid}
      />
      <RateRow
        id="bulkDailyRate"
        label="Daily/flat rate"
        suffix="/day"
        enabled={changeDaily}
        onToggle={setChangeDaily}
        value={daily}
        onChange={setDaily}
        invalid={dailyInvalid}
      />

      {clearing.length > 0 && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
          Leaving the {clearing.join(' and ')} rate blank will <strong>clear</strong> it for all{' '}
          {selectedCount} {noun}. Reports will show them as having no rate set.
        </p>
      )}
    </FormModal>
  );
}

interface RateRowProps {
  id: string;
  label: string;
  suffix: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
}

function RateRow({ id, label, suffix, enabled, onToggle, value, onChange, invalid }: RateRowProps) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="h-4 w-4 border-gray-300 text-rack focus:ring-rack"
        />
        Set {label.toLowerCase()}
      </label>
      <div className="relative">
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
        <input
          type="number"
          id={id}
          aria-label={label}
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={!enabled}
          placeholder={enabled ? 'Blank clears the rate' : '0.00'}
          className={`block w-full pl-7 pr-12 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:text-gray-400 ${
            invalid ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">{suffix}</span>
      </div>
      {invalid && <p className="mt-1 text-sm text-red-600">Enter a dollar amount of 0 or more.</p>}
    </div>
  );
}
